import { motion } from "framer-motion";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { ArrowDown, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/formatters";
import type { KpiSummary } from "@/api/analytics";

interface KpiCardsProps {
  kpis?: KpiSummary[];
  loading?: boolean;
}

/** Six-up KPI strip. Each card carries a label + big number + delta
 *  pill + a 24-px sparkline pulled from the API's `sparkline` series.
 *  Cards mount in cascade so the strip reads as a row, not a flash. */
export function KpiCards({ kpis, loading }: KpiCardsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-[92px] animate-pulse rounded-plane bg-white shadow-plane"
          />
        ))}
      </div>
    );
  }
  const safe = kpis ?? [];
  if (safe.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      {safe.map((kpi, idx) => (
        <motion.div
          // KPI key is canonical from the backend, but we also fall
          // back to the index so a missing field doesn't trip React
          // with a duplicate-key warning.
          key={kpi.key ?? `kpi-${idx}`}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.08, duration: 0.2, ease: "easeOut" }}
          className="rounded-plane bg-white p-4 shadow-plane"
        >
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            {kpi.label}
          </div>
          <div className="mt-1 flex items-baseline justify-between gap-2">
            <span className="text-[28px] font-semibold tabular-nums tracking-tight text-slate-900">
              {kpi.format === "percentage"
                ? `${(Math.round(kpi.value * 10) / 10).toFixed(1)}%`
                : formatNumber(kpi.value)}
            </span>
            {kpi.delta_pct != null && Number.isFinite(kpi.delta_pct) && (
              <DeltaPill delta={kpi.delta_pct} />
            )}
          </div>
          {kpi.sparkline && kpi.sparkline.length > 1 && (
            <div className="mt-2 h-6">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={kpi.sparkline}
                  margin={{ top: 1, right: 0, bottom: 1, left: 0 }}
                >
                  <defs>
                    <linearGradient
                      id={`spark-${kpi.key}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#6366f1"
                    strokeWidth={1.5}
                    fill={`url(#spark-${kpi.key})`}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
}

function DeltaPill({ delta }: { delta: number }) {
  // Round to one decimal but trim trailing zero.
  const rounded = Math.round(delta * 10) / 10;
  const positive = rounded > 0;
  const negative = rounded < 0;
  const Icon = positive ? ArrowUp : negative ? ArrowDown : null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
        positive && "bg-emerald-50 text-emerald-700",
        negative && "bg-rose-50 text-rose-700",
        !positive && !negative && "bg-slate-100 text-slate-500",
      )}
    >
      {Icon && <Icon className="h-2.5 w-2.5" />}
      {Math.abs(rounded)}%
    </span>
  );
}
